import { Component, OnDestroy, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subscription, filter } from 'rxjs';
import { CustomWafRule, DocumentationComponent, ShieldService } from '@bn/app-shield';
import { PlanType } from '@bn/app-shield/enums';
import { AppRoutes } from '@bn/dashboard-core/enums';
import { SharedEntitiesModule } from '@bn/shared/entities';
import { MetricFormatPipe } from '@bn/shared/entities/index';
import { ContentService } from '@bn/shared/services';
import { StoreRequest } from '@bn/shared/state-store';
import { AlertComponent } from '@bn/web-ui/alert/alert.component';
import { CardComponent } from '@bn/web-ui/card/card.component';
import { ChartType } from '@bn/web-ui/chart';
import { ConfirmationDialogComponent } from '@bn/web-ui/dialog';
import { DropdownMenuItem } from '@bn/web-ui/dropdown-menu';
import { LinkListItem } from '@bn/web-ui/link-list/models';
import { TableColumn, TableRow } from '@bn/web-ui/table';
import { TableComponent } from '@bn/web-ui/table/table.component';
import { ShieldApiStore } from '../../stores';

@Component({
  templateUrl: './app-shield-rate-limit.page.html',
  styleUrls: ['./app-shield-rate-limit.page.scss'],
  imports: [DocumentationComponent, AlertComponent, CardComponent, TableComponent, SharedEntitiesModule],
})
export class AppShieldRateLimitPage implements OnDestroy {
  private readonly dialog = inject(BsModalService);

  #router = inject(Router);
  #route = inject(ActivatedRoute);
  #shieldStore = inject(ShieldApiStore);
  #contentService = inject(ContentService);
  #shieldService = inject(ShieldService);
  #subscriptions = new Subscription();

  planType = PlanType;

  getSelectedShield = this.#shieldStore.selectedEntity();
  isDeleting = this.#shieldStore.isLoading(StoreRequest.Delete);
  rateLimitsPagination = this.#shieldStore.rateLimitsPagination;

  blockedBreaches = signal(0);
  challengedBreaches = signal(0);
  loggedBreaches = signal(0);
  totalBreaches = signal(0);

  rateLimitReached = false;
  nextPlan?: PlanType | null;
  tableData: TableRow[] | null = null;
  titleFirst = 'Documentation';
  linksFirst?: LinkListItem[] = [
    { text: 'Overview', url: 'https://docs.bunny.net/shield' },
    { text: 'Troubleshooting', url: 'https://docs.bunny.net/shield/troubleshooting' },
    { text: 'API Specifications', url: 'https://api.bunny.net/shield/docs/index.html' },
  ];
  currentPage = 1;
  readonly tableColumns: TableColumn[] = [
    {
      id: 'name',
      name: 'Name',
    },
    {
      id: 'activity',
      name: 'Activity',
    },
    {
      id: 'requests',
      name: 'Requests',
    },
    {
      id: 'actions',
      name: '',
    },
  ];

  constructor() {
    this.#shieldStore.clearSubmitted();
    this.#shieldStore.clearUpdated();

    const pullZoneId = this.#shieldStore.pullZoneId();

    this.#contentService.setTitle('Rate limiting');
    this.#contentService.setBackButton({
      routerLink: `/${AppRoutes.CDN}/${pullZoneId}/${AppRoutes.Shield}`,
    });

    this.#subscribeToRouteAndData();
    this.#subscribeToStoreSignals();
  }

  ngOnDestroy(): void {
    this.#contentService.setBadge(null);
    this.#contentService.setBackButton(null);
  }

  handlePageChange(page: number): void {
    this.#shieldStore.getRateLimits({ page });
  }

  onDropdownClick(event: DropdownMenuItem): void {
    switch (event.action) {
      case 'edit':
        this.#router.navigate([`edit/${event.id}`], { relativeTo: this.#route });
        break;
      case 'delete':
        this.deleteRateLimitRule(event.id as number);
        break;
      default:
        break;
    }
  }

  deleteRateLimitRule(ruleId: number): void {
    const confirmDialogRef = this.dialog.show(ConfirmationDialogComponent, {
      class: 'modal-dialog-centered modal-sm',
      initialState: {
        data: {
          contextType: 'danger',
          confirmButtonName: 'Yes',
          description: 'Are you sure you want to delete the rate limit rule.',
          event: {
            action: 'confirm',
          },
          title: 'Delete rate limit rule?',
          textCenter: true,
        },
      },
    });

    this.#subscriptions.add(
      confirmDialogRef.content?.getOnAction$().subscribe((event) => {
        if (event.action === 'cancel') return this.dialog.hide();

        this.#shieldStore.deleteRateLimitRule({ ruleId });
      }),
    );
  }

  #subscribeToRouteAndData(): void {
    this.#subscriptions.add(
      this.#route.queryParams.subscribe({
        next: (params) => {
          this.currentPage = Number(params.page);
        },
      }),
    );
  }

  #subscribeToStoreSignals(): void {
    effect(() => {
      if (this.getSelectedShield()) {
        untracked(() => {
          this.#shieldStore.getRateLimits({ page: this.currentPage });
        });
      }
    });

    effect(() => {
      if (this.#shieldStore.rateLimits()) {
        const currentLimitsCount = this.#shieldStore?.rateLimitsPagination()?.totalCount || 0;
        this.nextPlan = this.#shieldService.getNextPlanByRateLimit(currentLimitsCount);
        this.rateLimitReached = (this.getSelectedShield()?.rateLimitRulesLimit ?? 0) - currentLimitsCount === 0;
        this.tableData = this.#shieldStore.rateLimits().map((item) => this.#createTableRow(item));
      }

      const plan = this.getSelectedShield()?.planType || 0;
      if (plan > PlanType.Basic) {
        const planName = PlanType[plan];

        this.#contentService.setBadge({
          contextType: 'premium',
          text: planName,
          tooltip: `You have Bunny Shield ${planName} enabled`,
        });
      } else {
        this.#contentService.setBadge(null);
      }

      // When receiving chart data, update table with it
      if (this.#shieldStore.chartMetrics()) {
        const rowMetrics = this.#shieldStore.chartMetrics().perRateLimitChartData;

        (this.tableData ?? []).forEach((row) => {
          const chartMetric = rowMetrics[String(row.itemId)];
          const columnMiniChart = row.columns.find(({ id }) => id === 'activity');
          const columnRequests = row.columns.find(({ id }) => id === 'requests');
          // set mini chart data
          if (columnMiniChart && columnMiniChart.chart) {
            // columnMiniChart.chart.contextType = 'success'; // TODO: one day when context is known
            columnMiniChart.chart.options = {
              dataset: {
                // Note: We have 5 values in array: time, http request, logged, blocked, mitigated - we need to aggregate last 3 which are childs to https requests to get correct value
                source: (chartMetric?.chartSeries ?? []).slice(1).map((values) => [values[0], values.slice(2).reduce((sum, v) => Number(sum) + Number(v), 0)]),
              },
            };
          }
          // set requests data
          if (columnRequests && columnRequests.badge) {
            // columnRequests.badge.contextType = 'success'; // TODO: one day when context is known
            columnRequests.badge.value = MetricFormatPipe.metricFormat(chartMetric?.total.totalBreaches ?? 0).formatted;
          }
        });

        // Trigger change detection
        if (this.tableData) this.tableData = [...this.tableData];
      }
    });

    toObservable(this.#shieldStore.chartMetrics)
      .pipe(takeUntilDestroyed())
      .subscribe((chartMetrics) => {
        const { blockedBreaches, challengedBreaches, loggedBreaches, totalBreaches } = chartMetrics.rateLimitChartData.total;

        this.blockedBreaches.set(blockedBreaches);
        this.challengedBreaches.set(challengedBreaches);
        this.loggedBreaches.set(loggedBreaches);
        this.totalBreaches.set(totalBreaches);
      });

    toObservable(this.getSelectedShield)
      .pipe(filter((shield) => !!shield))
      .pipe(takeUntilDestroyed())
      .subscribe((shield) => {
        const plan = shield?.planType || 0;

        if (plan > PlanType.Basic) {
          const planName = PlanType[plan];

          this.#contentService.setBadge({
            contextType: 'premium',
            text: planName,
            tooltip: `You have Bunny Shield ${planName} enabled`,
          });
        } else {
          this.#contentService.setBadge(null);
        }
      });
  }

  #createTableRow(rateLimit: CustomWafRule): TableRow {
    return {
      itemId: rateLimit.id,
      columns: [
        {
          id: 'name',
          value: `${rateLimit.ruleName}`,
          valueType: 'string',
        },
        {
          id: 'activity',
          valueType: 'chart',
          chart: {
            type: ChartType.MiniLine,
            contextType: 'light',
            width: '290px',
            options: {
              dataset: {
                source: [],
              },
            },
          },
        },
        {
          id: 'requests',
          valueType: 'badge',
          badge: {
            type: 'flat',
            value: '0',
            contextType: 'info',
          },
        },
        {
          id: 'actions',
          valueType: 'actions',
          dropdownMenuItems: [
            {
              label: 'Edit',
              icon: 'edit',
              action: 'edit',
              id: rateLimit.id,
            },
            {
              label: 'Delete',
              icon: 'trash-can',
              action: 'delete',
              id: rateLimit.id,
            },
          ],
        },
      ],
    };
  }
}
